// MARK://dhk8/pos/pages/LoginPage
// PIN-first login. Phase 1: 4-digit ID. Phase 2: 6-digit password.
// Email fallback shown via toggle (hidden in kiosk mode).
// Port of sc-restaurant PinLoginPage.tsx — auth endpoint → platform-auth.dockethub.app.

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Delete, Check, Lock, User } from 'lucide-react'
import { useAuthStore } from '../stores/auth-store.js'
import { svcUrl } from '../lib/api.js'
import BrandMark from '../components/BrandMark.js'

type Phase = 'ID' | 'PASS'
const ID_LENGTH = 6
const PASS_LENGTH = 6
const MAX_FAILED = 3
/** Local persistence for the last successful PIN id so the user only has to type
 *  the 6-digit password on subsequent logins (kiosk-style flow). Cleared on logout
 *  (see auth-store.logout) and via the "Cambiar ID" link on the PASS phase. */
const LAST_PINID_KEY = 'dhk8-pos-last-pinid'

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isKiosk = searchParams.get('kiosk') === 'true'
  const returnTo = searchParams.get('returnTo') ?? '/floor'

  const { pinLogin, emailLogin, loading } = useAuthStore()

  // Hydrate the last successful PIN id from localStorage so the cajero only types
  // the password on the next login. We skip the pin-exists validation since the
  // pin-login endpoint will validate the (id, pass) pair anyway.
  const savedPinId = typeof window !== 'undefined' ? window.localStorage.getItem(LAST_PINID_KEY) : null
  const hasSavedPinId = !!(savedPinId && savedPinId.length === ID_LENGTH)
  const [mode, setMode] = useState<'pin' | 'email'>('pin')
  const [phase, setPhase] = useState<Phase>(hasSavedPinId ? 'PASS' : 'ID')
  const [pinId, setPinId] = useState(hasSavedPinId ? (savedPinId as string) : '')
  const [pinPassword, setPinPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [failedCount, setFailedCount] = useState(0)
  const [shake, setShake] = useState(false)

  // Email mode
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const locked = failedCount >= MAX_FAILED

  const reset = useCallback(() => {
    setPinId('')
    setPinPassword('')
    setPhase('ID')
    setError(null)
  }, [])

  const submit = useCallback(
    async (id: string, pass: string) => {
      setError(null)
      try {
        await pinLogin(id, pass)
        try { window.localStorage.setItem(LAST_PINID_KEY, id) } catch { /* storage disabled */ }
        navigate(returnTo, { replace: true })
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'PIN incorrecto'
        setError(msg)
        setFailedCount((c) => c + 1)
        setShake(true)
        setTimeout(() => setShake(false), 300)
        setPinPassword('')
        setPhase('PASS')
      }
    },
    [pinLogin, navigate, returnTo],
  )

  useEffect(() => {
    if (phase === 'PASS' && pinPassword.length === PASS_LENGTH && !loading && !locked) {
      void submit(pinId, pinPassword)
    }
  }, [phase, pinPassword, pinId, submit, loading, locked])

  function handleDigit(d: string) {
    if (locked || loading) return
    setError(null)
    if (phase === 'ID') {
      if (pinId.length < ID_LENGTH) {
        const next = pinId + d
        setPinId(next)
        // Al completar los 6 dígitos, valida contra DB antes de pedir la clave.
        // Evita que el cajero teclee la clave en vano cuando el ID está mal.
        if (next.length === ID_LENGTH) void verifyPinId(next)
      }
    } else {
      if (pinPassword.length < PASS_LENGTH) {
        setPinPassword(pinPassword + d)
      }
    }
  }

  async function verifyPinId(id: string) {
    try {
      const res = await fetch(svcUrl('dhk8-platform-auth', `/v1/auth/pin-exists?pinId=${encodeURIComponent(id)}`), {
        credentials: 'omit',
      })
      const body = await res.json().catch(() => ({ exists: false }))
      if (res.ok && body.exists === true) {
        setPhase('PASS')
        return
      }
      setError('ID no encontrado — verifica los 6 dígitos')
      setShake(true)
      setTimeout(() => setShake(false), 300)
      setPinId('')
    } catch {
      // Si la red falla, igual avanzamos — la validación es UX, no auth real.
      setPhase('PASS')
    }
  }

  function handleBackspace() {
    if (locked || loading) return
    if (phase === 'ID') {
      setPinId((v) => v.slice(0, -1))
    } else {
      if (pinPassword.length === 0) setPhase('ID')
      else setPinPassword((v) => v.slice(0, -1))
    }
  }

  // Teclado físico. La caja a veces no es touch — captura 0-9, Backspace, Escape
  // a nivel window pero solo en modo PIN (no en email-login, donde los inputs
  // del formulario ya consumen sus teclas). Ignora cuando el foco está en otro
  // input/textarea editable para no interferir.
  useEffect(() => {
    if (mode !== 'pin') return
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key >= '0' && e.key <= '9') { e.preventDefault(); handleDigit(e.key); return }
      if (e.key === 'Backspace') { e.preventDefault(); handleBackspace(); return }
      if (e.key === 'Escape') { e.preventDefault(); reset(); return }
    }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, phase, pinId, pinPassword, locked, loading])

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await emailLogin(email, password)
      navigate(returnTo, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Credenciales inválidas')
    }
  }

  return (
    <div className="dhk8-pin-root">
      <div className={`dhk8-pin-card${shake ? ' dhk8-pin-card--shake' : ''}`}>
        <div className="dhk8-pin-brand">
          <BrandMark size="lg" />
          <span className="dhk8-pin-tag">POS</span>
        </div>

        {mode === 'pin' ? (
          <>
            <div className="dhk8-pin-phase">
              {phase === 'ID' ? (
                <><User size={14} /><span>Ingresa tu ID (6 dígitos)</span></>
              ) : (
                <>
                  <Lock size={14} />
                  <span>Ingresa tu clave (6 dígitos)</span>
                  <button
                    type="button"
                    className="dhk8-pin-changeid"
                    onClick={() => {
                      try { window.localStorage.removeItem(LAST_PINID_KEY) } catch { /* noop */ }
                      reset()
                    }}
                    aria-label="Cambiar ID"
                  >
                    Cambiar ID
                  </button>
                </>
              )}
            </div>

            <div className="dhk8-pin-dots" aria-live="polite">
              {Array.from({ length: phase === 'ID' ? ID_LENGTH : PASS_LENGTH }).map((_, i) => {
                const filled = phase === 'ID' ? i < pinId.length : i < pinPassword.length
                return <span key={i} className={`dhk8-pin-dot${filled ? ' dhk8-pin-dot--on' : ''}`} />
              })}
            </div>

            {error && <div className="dhk8-pin-error">{error}</div>}
            {locked && <div className="dhk8-pin-locked">Espera 15 min o pide al admin que te desbloquee.</div>}

            <div className="dhk8-pin-keypad" aria-disabled={locked || loading}>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} type="button" className="dhk8-pin-key"
                  onClick={() => handleDigit(d)} disabled={locked || loading} aria-label={`Dígito ${d}`}>
                  {d}
                </button>
              ))}
              <button type="button" className="dhk8-pin-key dhk8-pin-key--ghost"
                onClick={handleBackspace} disabled={locked || loading} aria-label="Borrar">
                <Delete size={20} />
              </button>
              <button type="button" className="dhk8-pin-key"
                onClick={() => handleDigit('0')} disabled={locked || loading} aria-label="Dígito 0">
                0
              </button>
              <button type="button" className="dhk8-pin-key dhk8-pin-key--ghost dhk8-pin-key--ok"
                onClick={reset} disabled={loading} aria-label="Reiniciar">
                <Check size={20} />
              </button>
            </div>

            {!isKiosk && (
              <div className="dhk8-pin-toggle">
                <button type="button" onClick={() => setMode('email')}>
                  Usar correo y contraseña
                </button>
              </div>
            )}

            {/* Dev-only bypass: enter without a backend (build with VITE_DEV_LOGIN=1).
                Never present in a production build. */}
            {import.meta.env.VITE_DEV_LOGIN === '1' && (
              <div className="dhk8-pin-toggle">
                <button type="button" onClick={() => {
                  if (typeof document !== 'undefined') {
                    document.cookie = 'dhk8_tenant=00000000-0000-0000-0000-000000000001;path=/;samesite=lax'
                    document.cookie = 'dhk8_token=dev;path=/;samesite=lax'
                  }
                  useAuthStore.setState({
                    user: { id: '00000000-0000-0000-0000-0000000000d0', email: 'dev@local', fullName: 'Mesero Demo', role: 'WAITER', branchId: '00000000-0000-0000-0000-0000000000b1' },
                  })
                  navigate(returnTo, { replace: true })
                }}>
                  Entrar (modo demo · sin backend)
                </button>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleEmailSubmit} className="dhk8-pin-email-form">
            <div className="dhk8-pin-field">
              <label>Correo</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com" autoFocus required />
            </div>
            <div className="dhk8-pin-field">
              <label>Contraseña</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required />
            </div>
            {error && <div className="dhk8-pin-error">{error}</div>}
            <button type="submit" className="dhk8-pin-submit" disabled={loading}>
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
            <div className="dhk8-pin-toggle">
              <button type="button" onClick={() => setMode('pin')}>Usar PIN</button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
